/**
 * Generación conjunta PDF + Excel de diferencias confronta, con progreso y envío por correo.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileSearch, FileSpreadsheet, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SpellcheckInput from "@/components/SpellcheckInput";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";

const POLL_MS = 1500;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clientEmails(client) {
  if (!client) return [];
  const out = [];
  for (const k of ["email", "email_principal", "email_alterno", "owner_email"]) {
    const e = String(client[k] || "").trim().toLowerCase();
    if (e && !out.includes(e)) out.push(e);
  }
  return out;
}

function isValidEmail(value) {
  const v = String(value || "").trim().toLowerCase();
  return Boolean(v) && EMAIL_RE.test(v);
}

function smtpConfigAlert({ transportReady, passwordEffective }) {
  if (transportReady) return null;
  if (passwordEffective === false) {
    return (
      "La contraseña de correo no se puede leer en este servidor. " +
      "Vuelva a guardarla en Parámetros de correo."
    );
  }
  return "Configure el envío en Despacho → Calendario → Recordatorios → Parámetros de correo.";
}

export default function ConfrontaDiferenciasBundleDialog({
  open,
  onOpenChange,
  clientId,
  client,
  fechaDesde,
  fechaHasta,
  direccion = "ingreso",
}) {
  const [phase, setPhase] = useState("idle");
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [jobId, setJobId] = useState("");
  const [pdfFilename, setPdfFilename] = useState("");
  const [xlsxFilename, setXlsxFilename] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [appendFooter, setAppendFooter] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [transportReady, setTransportReady] = useState(false);
  const [smtpMeta, setSmtpMeta] = useState({
    smtpHost: "",
    smtpUser: "",
    hasSmtpPassword: false,
    fromEmail: "",
    passwordEffective: true,
  });
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadEmailDraft = useCallback(async ({ suggestDryRun = true } = {}) => {
    if (!clientId || !fechaDesde || !fechaHasta) return;
    try {
      const { data } = await api.get("/reports/confronta-diferencias-mes-bundle/email-draft", {
        params: {
          client_id: clientId,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          direccion,
        },
      });
      setSubject(data.subject || "");
      setBody(data.body || "");
      const ready = Boolean(data.transport_ready);
      setTransportReady(ready);
      setSmtpMeta({
        smtpHost: data.smtp_host || "",
        smtpUser: data.smtp_user || "",
        hasSmtpPassword: Boolean(data.has_smtp_password),
        fromEmail: data.from_email || "",
        passwordEffective: data.smtp_password_effective !== false,
      });
      if (suggestDryRun) {
        setDryRun(!ready);
      } else if (ready) {
        setDryRun(false);
      }
      const emails = data.to_emails?.length ? data.to_emails : clientEmails(client);
      setToEmail(data.suggested_to || emails[0] || "");
    } catch (e) {
      const emails = clientEmails(client);
      setToEmail(emails[0] || "");
      setDryRun(true);
    }
  }, [clientId, client, fechaDesde, fechaHasta, direccion]);

  const pollJob = useCallback(
    (id) => {
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/reports/confronta-diferencias-mes-bundle/${id}`);
          setPercent(data.percent ?? 0);
          setMessage(data.message || "");
          if (data.status === "done") {
            stopPoll();
            setPhase("ready");
            setErrorDetail("");
            setPdfFilename(data.pdf_filename || "diferencias.pdf");
            setXlsxFilename(data.xlsx_filename || "diferencias.xlsx");
            await loadEmailDraft();
          } else if (data.status === "error") {
            stopPoll();
            setPhase("error");
            setErrorDetail(data.error || data.message || "Error al generar el reporte");
            toast.error(data.error || "Error al generar el reporte");
          }
        } catch (e) {
          stopPoll();
          setPhase("error");
          const detail =
            formatApiError(e.response?.data?.detail) || "Error consultando el progreso";
          setErrorDetail(detail);
          toast.error(detail);
        }
      }, POLL_MS);
    },
    [loadEmailDraft, stopPoll],
  );

  const startGeneration = useCallback(async () => {
    if (!clientId || !fechaDesde || !fechaHasta) return;
    setPhase("generating");
    setPercent(0);
    setMessage("Iniciando…");
    setErrorDetail("");
    setJobId("");
    try {
      const { data } = await api.post("/reports/confronta-diferencias-mes-bundle/start", {
        client_id: clientId,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        direccion,
      });
      setJobId(data.job_id);
      pollJob(data.job_id);
    } catch (e) {
      setPhase("error");
      const detail =
        formatApiError(e.response?.data?.detail) || "No se pudo iniciar la generación";
      setErrorDetail(detail);
      toast.error(detail);
    }
  }, [clientId, fechaDesde, fechaHasta, direccion, pollJob]);

  useEffect(() => {
    if (!open) {
      stopPoll();
      setPhase("idle");
      setPercent(0);
      setMessage("");
      setErrorDetail("");
      setJobId("");
      return undefined;
    }
    loadEmailDraft();
    startGeneration();
    return () => stopPoll();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadArtifact = async (kind) => {
    if (!jobId) return;
    try {
      const { data } = await api.get(`/reports/confronta-diferencias-mes-bundle/${jobId}/${kind}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      if (kind === "pdf") {
        const popup = window.open(url, "_blank");
        if (!popup) toast.error("Habilita ventanas emergentes para ver el PDF");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = xlsxFilename || "diferencias.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 180000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar el archivo");
    }
  };

  const smtpAlert = useMemo(
    () =>
      smtpConfigAlert({
        transportReady,
        passwordEffective: smtpMeta.passwordEffective,
      }),
    [transportReady, smtpMeta.passwordEffective],
  );

  const emailValid = isValidEmail(toEmail);
  const formReady = emailValid && subject.trim() && body.trim();

  const handleSendEmail = async () => {
    if (!jobId) return;
    if (!emailValid) {
      toast.error("Indique un correo destino válido (debe incluir @ y dominio).");
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error("Complete asunto y mensaje.");
      return;
    }
    setSending(true);
    try {
      if (!dryRun) {
        await loadEmailDraft({ suggestDryRun: false });
      }
      const { data } = await api.post(`/reports/confronta-diferencias-mes-bundle/${jobId}/send-email`, {
        to_email: toEmail.trim(),
        subject: subject.trim(),
        body: body.trim(),
        append_footer: appendFooter,
        dry_run: dryRun,
      });
      const result = data.result || {};
      if (data.ok) {
        toast.success(`Correo enviado a ${data.to_email}`);
      } else if (data.dry_run || result.status === "dry_run") {
        toast.message("Simulación: no se envió correo.", {
          description: `Destino: ${data.to_email} · adjuntos: PDF y Excel`,
        });
      } else if (result.status === "not_configured") {
        toast.error(result.detail || "SMTP no configurado. Complete Parámetros de correo en Despacho.");
      } else {
        toast.error(result.detail || "No se pudo enviar el correo");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar el correo");
    } finally {
      setSending(false);
    }
  };

  const clientLabel = client?.razon_social || client?.nombre || "Cliente";
  const flujoLabel =
    direccion === "nomina" ? "nóminas" : direccion === "egreso" ? "egresos" : "ingresos";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="confronta-diferencias-bundle-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Reporte de diferencias — {flujoLabel} (PDF + Excel)</DialogTitle>
          <DialogDescription className="text-xs">
            {clientLabel}
            {client?.rfc ? ` · ${client.rfc}` : ""} — {fechaDesde} → {fechaHasta}
          </DialogDescription>
        </DialogHeader>

        {(phase === "generating" || phase === "idle") && (
          <div className="space-y-3 py-2" data-testid="confronta-bundle-progress">
            <p className="text-xs text-slate-600 m-0">{message || "Preparando confronta por mes…"}</p>
            <Progress value={percent} className="h-2" />
            <p className="text-[10px] text-slate-500 m-0">{percent}% — puede tardar varios minutos en periodos largos.</p>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              Generando PDF y Excel en el servidor…
            </div>
          </div>
        )}

        {phase === "error" && (
          <div className="py-2 space-y-2" data-testid="confronta-bundle-error">
            <p className="text-xs text-rose-700 m-0">No se pudo completar la generación.</p>
            {errorDetail ? (
              <p className="text-[11px] text-rose-800/90 bg-rose-50 border border-rose-100 rounded-sm px-2 py-1.5 m-0 break-words">
                {errorDetail}
              </p>
            ) : null}
            <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={startGeneration}>
              Reintentar
            </Button>
          </div>
        )}

        {phase === "ready" && (
          <div className="space-y-4 py-1">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm text-xs border-rose-300 text-rose-700"
                data-testid="confronta-bundle-dl-pdf"
                onClick={() => downloadArtifact("pdf")}
              >
                <FileSearch className="w-3.5 h-3.5 mr-1.5" />
                Descargar PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm text-xs border-[#0F2D5C] text-[#0F2D5C]"
                data-testid="confronta-bundle-dl-xlsx"
                onClick={() => downloadArtifact("xlsx")}
              >
                <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                Descargar Excel
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-3" data-testid="confronta-bundle-email">
              <p className="text-xs font-semibold text-slate-800 m-0 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Enviar al cliente
              </p>
              <div
                className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2"
                data-testid="confronta-bundle-remitente"
              >
                <p className="text-[11px] font-semibold text-slate-800 m-0">Datos del remitente</p>
                <p className="text-xs text-slate-600 m-0 mt-1">
                  {smtpMeta.fromEmail || "Sin correo configurado"}
                </p>
              </div>
              {smtpAlert ? (
                <p
                  className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 m-0"
                  data-testid="confronta-bundle-smtp-alert"
                >
                  {smtpAlert}
                </p>
              ) : null}
              <div>
                <Label className="text-xs">Correo destino</Label>
                <Input
                  type="email"
                  className="mt-1 h-9 text-sm rounded-sm"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="cliente@empresa.com"
                />
                {toEmail.trim() && !emailValid ? (
                  <p className="text-[10px] text-rose-700 mt-1 m-0">Correo inválido: use formato nombre@dominio.com</p>
                ) : null}
              </div>
              <div>
                <Label className="text-xs">Asunto</Label>
                <SpellcheckInput
                  className="mt-1 h-9 text-sm rounded-sm"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  testId="confronta-bundle-subject"
                  wrapperClassName="mt-1 space-y-1"
                />
              </div>
              <div>
                <Label className="text-xs">Mensaje</Label>
                <SpellcheckTextarea
                  className="mt-1 min-h-[120px] text-sm rounded-sm"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                <label className="flex items-center gap-2">
                  <Checkbox checked={appendFooter} onCheckedChange={(v) => setAppendFooter(Boolean(v))} />
                  Incluir pie de firma del despacho
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={dryRun}
                    onCheckedChange={(v) => setDryRun(Boolean(v))}
                    data-testid="confronta-bundle-dry-run"
                  />
                  Solo simular (no enviar)
                </label>
              </div>
              <Button
                type="button"
                size="sm"
                className="rounded-sm"
                disabled={sending || !formReady}
                data-testid="confronta-bundle-send-email"
                onClick={handleSendEmail}
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                )}
                {dryRun ? "Simular envío PDF + Excel" : "Enviar PDF y Excel por correo"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
